import { JSX, useContext, useEffect, useMemo, useState } from "react";
import { ColumnConfig, SortDirections, wafflesKeyColumnName } from "./waffles";
import ResourceTaggingService from "../resource-tagging/resourceTaggingService";
import { ManageResourceTagDisplayModel } from "../tags/types";
import { AdvancedExclusionsContext } from "../projects/projection-provider/advanced-exclusions/AdvancedExclusionsContext";
import { Waffles } from "./waffles/Waffles";
import { SharedBulkSelectProject } from "@app/projects/projection-provider/advanced-exclusions/sharedBulkSelectProject";

interface BulkProjectSelectTagsTableProps {
  appendCurrentProjectToList: (projectList: string[]) => void;
  searchString?: string;
  selections: string[];
  setSelections: (selections: string[]) => void;
};

interface TagToProjectsList {
  tag: string;
  projectsList: SharedBulkSelectProject[];
}

interface GroupTagsTableData {
  [wafflesKeyColumnName]: string;
  tagGroup: string;
  tagsInGroup: string[];
  tag: string;
  tagsToProjects: TagToProjectsList[];
  numberOfProjects: number;
}

/* oxlint-disable sort-keys */
const groupTagsColumnConfig: ColumnConfig = {
  tagGroup: {
    displayText: "Tag Groups",
    testId: "tag-group",
  },
  tag: {
    displayText: "Tags",
    testId: "tags",
  },
  numberOfProjects: {
    displayText: "Number of Projects",
    testId: "number-of-projects"
  },
}
/* oxlint-enable sort-keys */

const customSortTags = (rows: GroupTagsTableData[], key: keyof GroupTagsTableData, direction: SortDirections) => {
  const directionMultiplier = direction === SortDirections.Asc ? 1 : -1;
  switch (key) {
    case "tag":
    case "tagGroup":
    default:
      return rows.sort((a, b) => (a[key] > b[key] ? 1 : -1) * directionMultiplier);
  }
}

interface TagLookup {
  [tagName: string]: SharedBulkSelectProject[];
}

const BulkProjectSelectTagsTable = (props: BulkProjectSelectTagsTableProps): JSX.Element => {
  const resourceTaggingService = new ResourceTaggingService();

  const [groupTagsList, setGroupTagsList] = useState<ManageResourceTagDisplayModel[]>();
  const [originTagGroupData, setOriginTagGroupData] = useState<GroupTagsTableData[]>();
  const [selectedTagGroups, setSelectedTagGroups] = useState<string[]>(props.selections);
  const [tagLookup, setTagLookup] = useState<TagLookup>({});
  const [isLoading, setIsLoading] = useState(false);

  const wizardContext = useContext(AdvancedExclusionsContext);
  const currentProjectExists = wizardContext.currentProjectId != null && wizardContext.currentProjectId !== "";

  useEffect(() => {
    setIsLoading(true);
    resourceTaggingService.getClientProjectGroupTagInfo().then((response: ManageResourceTagDisplayModel[]) => {
      setGroupTagsList(response);
      setIsLoading(false);
    });
  }, [])

  useEffect(() => {
    const newTagLookup: TagLookup = {};
    const allTags = [...new Set<string>(wizardContext.projects.flatMap((p) => p.tags))].filter((tag) => tag !== undefined);
    allTags.forEach((t) => {
      const projects: SharedBulkSelectProject[] = [];
      wizardContext.projects.forEach((p) => {
        if (p.tags?.includes(t) && !p.rowIsDisabled && (!currentProjectExists || p.projectId.toString() != wizardContext.currentProjectId)) {
          projects.push(p);
        }
      })
      newTagLookup[t] = projects;
    });

    setTagLookup(newTagLookup);
  }, [wizardContext.projects]);

  /* oxlint-disable sort-keys */
  const groupsTableData: GroupTagsTableData[] = useMemo(() => {
    if (!!props.searchString) {
      const escapedSearchString = props.searchString.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
      const regex = new RegExp(escapedSearchString, "i");
      const filteredGroupsTableData = originTagGroupData?.filter(group => !!group?.tagGroup?.match(regex)
        || group.tagsToProjects.map(x => x.tag.toLocaleLowerCase()).some(p => !!p.match(regex)));

      return filteredGroupsTableData?.map<GroupTagsTableData>((group) => {

        return ({
          [wafflesKeyColumnName]: group.tagGroup.replaceAll(" ", "-"),
          tagGroup: group.tagGroup,
          tag: "",
          tagsInGroup: group.tagsInGroup,
          tagsToProjects: group.tagsToProjects,
          numberOfProjects: group.numberOfProjects
        })
      })
    }

    let tagGroupsWithProjects: GroupTagsTableData[] = [];
    const distinctTagGroups = [...new Set(groupTagsList?.map(x => x.tagGroupName))];
    if (distinctTagGroups?.length > 0) {
      distinctTagGroups?.forEach(tagGroup => {

        tagGroupsWithProjects.push({
          tagGroup: tagGroup,
          tag: "",
          tagsInGroup: [],
          tagsToProjects: [],
          numberOfProjects: 0,
          [wafflesKeyColumnName]: ""
        })
      });

      groupTagsList?.forEach(groupTag => {
        if (tagGroupsWithProjects.find(x => x.tagGroup === groupTag.tagGroupName).tagsInGroup.findIndex(x => x === groupTag.tagName) < 0) {
          tagGroupsWithProjects.find(x => x.tagGroup === groupTag.tagGroupName).tagsInGroup.push(groupTag.tagName);
        }
      });

      wizardContext.projects?.filter(project => !project.rowIsDisabled && (!currentProjectExists || project.projectId.toString() != wizardContext.currentProjectId))?.forEach(project => {
        project.tags?.forEach(tag => {
          tagGroupsWithProjects.forEach(tagGroupWithProjects => {
            if (tagGroupWithProjects.tagsInGroup.includes(tag)) {
              if (tagGroupWithProjects.tagsToProjects.findIndex(x => x.tag.toLocaleLowerCase() === tag.toLocaleLowerCase()) >= 0) {
                const tagIndex = tagGroupWithProjects.tagsToProjects.findIndex(x => x.tag === tag);
                tagGroupWithProjects?.tagsToProjects[tagIndex]?.projectsList?.push(project);
              }
              else {
                const newTagToProjects: TagToProjectsList = {
                  tag: tag,
                  projectsList: [project]
                }
                tagGroupWithProjects.tagsToProjects.push(newTagToProjects);
              }
            }
          })
        });
      });
    }

    tagGroupsWithProjects.forEach(tagGroupWithProjects => {
      if (tagGroupWithProjects.tagsToProjects.length <= 0) {
        tagGroupsWithProjects = tagGroupsWithProjects.filter(x => x.tagGroup != tagGroupWithProjects.tagGroup);
      }

    });


    const sortedByTagGroup = [...tagGroupsWithProjects].sort((a, b) => a.tagGroup.localeCompare(b.tagGroup));
    sortedByTagGroup.forEach(group => {
      group.tagsToProjects.sort((a, b) => a.tag.localeCompare(b.tag));
    });

    setOriginTagGroupData(sortedByTagGroup);

    return sortedByTagGroup.map<GroupTagsTableData>((group) => {
      let numProj = 0;
      group.tagsToProjects.forEach(tagToProjects => {
        numProj += tagToProjects.projectsList.length;
      });

      group.numberOfProjects = numProj;

      return ({
        [wafflesKeyColumnName]: group.tagGroup.replaceAll(" ", "-"),
        tagGroup: group.tagGroup,
        tag: "",
        tagsInGroup: group.tagsInGroup,
        tagsToProjects: group.tagsToProjects,
        numberOfProjects: group.numberOfProjects
      })
    })
  }, [groupTagsList, wizardContext.projects, tagLookup, props.searchString]);
  /* oxlint-enable sort-keys */

  const groupTagsExpansionData: Map<string, GroupTagsTableData[]> = useMemo(() => {
    const data = new Map<string, GroupTagsTableData[]>();
    groupsTableData
      .forEach(group => {
        /* oxlint-disable sort-keys */
        data.set(group.tagGroup.replaceAll(" ", "-"), group.tagsToProjects.map((tagToProjects) => (
          {
            [wafflesKeyColumnName]: group.tagGroup.replaceAll(" ", "-") + ":" + tagToProjects.tag.replaceAll(" ", "-"),
            tagGroup: "",
            tag: tagToProjects.tag,
            tagsInGroup: group.tagsInGroup,
            tagsToProjects: group.tagsToProjects,
            numberOfProjects: tagToProjects.projectsList.length
          }
        )));
        /* oxlint-enable sort-keys */
      });
    return data;
  }, [groupsTableData, wizardContext.projects, tagLookup]);

  const handleCheckboxChange = (selectedProjectIds: string[]) => {
    const selectedTagGroups: string[] = [];
    setSelectedTagGroups(selectedProjectIds);
    props.setSelections(selectedProjectIds);

    const selectedTags: string[] = [];
    selectedProjectIds.forEach(selection => {
      if (!selectedTagGroups.includes(selection.split(":")[0].replaceAll('-', ' '))) {
        selectedTagGroups.push(selection.split(":")[0].replaceAll('-', ' '));
      }
      if (!selectedTags.includes(selection.replaceAll('-', ' ').split(":")[1])) {
        selectedTags.push(selection.split(":")[1].replaceAll('-', ' '));
      }
    });

    const selectedProjectIdsList: string[] = [];
    groupsTableData.forEach(element => {

      if (selectedTagGroups.includes(element.tagGroup)) {
        element.tagsToProjects.forEach(tagToProjects => {
          if (selectedTags.includes(tagToProjects.tag)) {
            selectedProjectIdsList.push(...selectedProjectIdsList, ...tagToProjects.projectsList.filter(x => x.rowIsDisabled === false).map(x => x.projectId.toString()));
          }
        });
      }
    });

    const uniqueProjectIdsList: string[] = [...new Set(selectedProjectIdsList)];
    props.appendCurrentProjectToList(uniqueProjectIdsList);
    wizardContext.updateSelectedProjects(uniqueProjectIdsList);
  }

  const indeterminateKeys: string[] = useMemo(() => {
    const localIndeterminateKeys: string[] = [];
    const currentSelectedProjectIds = wizardContext.projects.filter((project) => project.isChecked).map((p) => p.projectId.toString());

    groupsTableData.forEach(group => {
      group.tagsToProjects.forEach(tagToProjects => {
        let matchingProjectIdCount = 0;
        tagToProjects.projectsList.forEach(project => {
          if (currentSelectedProjectIds.includes(project.projectId.toString())) {
            matchingProjectIdCount++;
          }
        });

        if (matchingProjectIdCount != tagToProjects.projectsList.length && matchingProjectIdCount > 0) {
          if (!localIndeterminateKeys.includes(group.tagGroup + ":" + tagToProjects.tag)) {
            localIndeterminateKeys.push(group.tagGroup + ":" + tagToProjects.tag);
          }
        }
        else if (matchingProjectIdCount == tagToProjects.projectsList.length && localIndeterminateKeys.includes(group.tagGroup + ":" + tagToProjects.tag)) {
          localIndeterminateKeys.filter(x => x != group.tagGroup + ":" + tagToProjects.tag);
        }
      });
    });

    return localIndeterminateKeys;
  }, [wizardContext.selectedProjectIds]);

  const numberOfProjectsInTagsView = useMemo(() => {
    // these are already filtered by the search value
    return Array.from(groupTagsExpansionData).reduce((result, [, values]) => {
      result += values.length;
      return result;
    }, 0);
  }, [groupTagsExpansionData]);

  const hasTags = useMemo(() => {
    return Object.entries(tagLookup).length > 0;
  }, [tagLookup]);

  return (
    <>
      {hasTags ? (
          <Waffles<GroupTagsTableData>
          columns={groupTagsColumnConfig}
          customSort={customSortTags}
          data={groupsTableData}
          dataExpansions={groupTagsExpansionData}
          disableProcessingOverlay={true}
          dataExpansionsHaveIndividualCheckboxes={true}
          fullWidth={true}
          loading={isLoading}
          handleCheckboxChange={handleCheckboxChange}
          indeterminateKeys={indeterminateKeys}
          showExpansionCheckboxesOnly={true}
          selectedKeys={selectedTagGroups}
          showCheckboxes={true}
          testId="bulk-project-select-tags-table"
          totalNumberOfRecords={numberOfProjectsInTagsView}
        />) :
        (<p>None of the projects matching your selections have any tags.</p>)
      }
    </>
  );
};

export default BulkProjectSelectTagsTable;